import type { LicenseResponseBody } from '@/features/Licenses/api/licenseHooks'
import { isSelfServiceLicense } from '@/features/Licenses/lib/licensePredicates'
import type { TFunction } from 'i18next'

export type LicenseActionId =
  | 'editEntitlements'
  | 'editMetadata'
  | 'renew'
  | 'reactivate'
  | 'revoke'

/**
 * `visible` is a UX-correctness layer ("does this action belong on screen now?")
 * separate from `enabled` (API-correctness — "would the API accept this call?").
 * They are not the same thing: a Renew button on an ACTIVE license that's still
 * 11 months from expiry is *enabled* (the API would accept it) but not *visible*
 * (operationally pointless, just clutter).
 */
export type LicenseAction = {
  id: LicenseActionId
  label: string
  enabled: boolean
  visible: boolean
  actionDisabledReason?: string
}

/** Window during which Renew surfaces while a license is still ACTIVE. */
export const RENEW_VISIBILITY_WINDOW_DAYS = 30

const MS_PER_DAY = 1000 * 60 * 60 * 24

/**
 * Derives the lifecycle actions for a license based on its status and
 * proximity-to-expiry.
 *
 * `enabled` rules (APP-1408 M1):
 *   - editEntitlements: enabled only when status === 'ACTIVE' and license is not self-service
 *   - editMetadata: always enabled
 *   - renew: enabled only when status === 'ACTIVE'
 *   - revoke: enabled only when status === 'ACTIVE'; provides distinct
 *     disabled reasons for REVOKED vs EXPIRED so operators see why.
 *
 * `visible` rules (UX layer on top, separate from `enabled`):
 *   - editEntitlements / editMetadata / revoke: always visible (revoke shows
 *     disabled with a tooltip outside ACTIVE so operators learn why it can't
 *     be applied — they will try)
 *   - renew: hidden unless ACTIVE-and-expiring within
 *     RENEW_VISIBILITY_WINDOW_DAYS, OR status === 'EXPIRED'. ACTIVE licenses
 *     far from expiry don't need a Renew button cluttering the page.
 */
export function deriveActions(
  license: LicenseResponseBody,
  t: TFunction,
  now: number = Date.now(),
): LicenseAction[] {
  const { status } = license
  const ns = { ns: 'features' } as const

  const daysToExpiry = (license.validityEnd - now) / MS_PER_DAY
  const renewVisible =
    status === 'EXPIRED' ||
    (status === 'ACTIVE' && daysToExpiry <= RENEW_VISIBILITY_WINDOW_DAYS)

  const isSelfService = isSelfServiceLicense(license)

  const reactivateApplicable =
    (status === 'REVOKED' || status === 'EXPIRED') && !isSelfService

  let editEntitlementsDisabledReason: string | undefined
  if (isSelfService) {
    editEntitlementsDisabledReason = String(
      t('licenses.actions.disabledReason.entitlementsSelfService', ns),
    )
  } else if (status !== 'ACTIVE') {
    editEntitlementsDisabledReason = String(
      t('licenses.actions.disabledReason.entitlementsNotActive', ns),
    )
  }

  let renewDisabledReason: string | undefined
  if (status !== 'ACTIVE') {
    renewDisabledReason = String(
      t('licenses.actions.disabledReason.renewNotActive', ns),
    )
  }

  let revokeDisabledReason: string | undefined
  if (status === 'REVOKED') {
    revokeDisabledReason = String(
      t('licenses.actions.disabledReason.alreadyRevoked', ns),
    )
  } else if (status === 'EXPIRED') {
    revokeDisabledReason = String(
      t('licenses.actions.disabledReason.revokeExpired', ns),
    )
  }

  return [
    {
      id: 'editEntitlements',
      label: String(t('licenses.actions.editEntitlements.label', ns)),
      enabled: status === 'ACTIVE' && !isSelfService,
      visible: true,
      actionDisabledReason: editEntitlementsDisabledReason,
    },
    {
      id: 'editMetadata',
      label: String(t('licenses.actions.editMetadata.label', ns)),
      enabled: true,
      visible: true,
      actionDisabledReason: undefined,
    },
    {
      id: 'renew',
      label: String(t('licenses.actions.renew.label', ns)),
      enabled: status === 'ACTIVE',
      visible: renewVisible,
      actionDisabledReason: renewDisabledReason,
    },
    {
      id: 'reactivate',
      label: String(t('licenses.actions.reactivate.label', ns)),
      enabled: reactivateApplicable,
      visible: reactivateApplicable,
      actionDisabledReason: undefined,
    },
    {
      id: 'revoke',
      label: String(t('licenses.actions.revoke.label', ns)),
      enabled: status === 'ACTIVE',
      visible: true,
      actionDisabledReason: revokeDisabledReason,
    },
  ]
}
